/**
 * Persistencia local de archivos de audio en IndexedDB — Fase radio fix.
 *
 * Las blob URLs (`URL.createObjectURL`) NO sobreviven a una navegación
 * o reload — son referencias en memoria del documento actual. Para
 * que el usuario no tenga que re-elegir la carpeta cada vez, guardamos
 * los archivos (Blob) en IndexedDB del navegador. Al re-montar la
 * radio, leemos los blobs de aquí y re-creamos URLs frescas.
 *
 * IndexedDB es **local al navegador del usuario**: vive en el disco del
 * cliente, scoped al origen del sitio, no se sincroniza entre
 * navegadores ni dispositivos. Si el usuario borra los datos del sitio
 * o entra en otro PC, tendrá que volver a elegir la carpeta — eso es
 * coherente con cómo funciona el file picker en general.
 *
 * Schema:
 *   DB     `51esc-radio`        version 1
 *   Store  `user-tracks`        keyPath `id`, valor: { id, name, blob, addedAt }
 *
 * Las operaciones son async pero no rechazan en errores transitorios —
 * loggean y siguen. Una falla puntual del IDB no debe romper la radio.
 */

const DB_NAME = '51esc-radio';
const DB_VERSION = 1;
const STORE_TRACKS = 'user-tracks';

/** Forma de un track persistido. Lo que entra y sale del store. */
export interface IdbTrackRecord {
  id: string;
  name: string;
  blob: Blob;
  addedAt: string;
}

let dbPromise: Promise<IDBDatabase | null> | null = null;

/**
 * Abre la BD (creándola si hace falta). Cacheamos la promesa para no
 * abrir N conexiones en N llamadas. Si IndexedDB no está disponible
 * (modo incógnito muy restrictivo, etc.), devolvemos null y todas las
 * operaciones siguientes se vuelven no-ops — la radio funciona igual,
 * solo se pierde la persistencia.
 */
function openDb(): Promise<IDBDatabase | null> {
  if (dbPromise) return dbPromise;
  dbPromise = new Promise<IDBDatabase | null>((resolve) => {
    if (typeof indexedDB === 'undefined') {
      console.warn('[radio/idb] IndexedDB no disponible en este navegador');
      resolve(null);
      return;
    }
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onupgradeneeded = (): void => {
      const db = req.result;
      if (!db.objectStoreNames.contains(STORE_TRACKS)) {
        db.createObjectStore(STORE_TRACKS, { keyPath: 'id' });
      }
    };
    req.onsuccess = (): void => resolve(req.result);
    req.onerror = (): void => {
      console.warn('[radio/idb] open failed:', req.error);
      resolve(null);
    };
    req.onblocked = (): void => {
      console.warn('[radio/idb] open blocked (otra pestaña con versión antigua)');
      resolve(null);
    };
  });
  return dbPromise;
}

/**
 * Guarda (o reemplaza) varios tracks en una sola transacción.
 * Más eficiente que llamadas individuales si el usuario sube muchos
 * archivos a la vez.
 */
export async function idbPutTracks(
  items: ReadonlyArray<{ id: string; name: string; blob: Blob }>,
): Promise<void> {
  const db = await openDb();
  if (!db) return;
  await new Promise<void>((resolve) => {
    const tx = db.transaction(STORE_TRACKS, 'readwrite');
    const store = tx.objectStore(STORE_TRACKS);
    const addedAt = new Date().toISOString();
    for (const it of items) {
      const rec: IdbTrackRecord = {
        id: it.id,
        name: it.name,
        blob: it.blob,
        addedAt,
      };
      store.put(rec);
    }
    tx.oncomplete = (): void => resolve();
    tx.onerror = (): void => {
      console.warn('[radio/idb] put tx error:', tx.error);
      resolve();
    };
    tx.onabort = (): void => {
      console.warn('[radio/idb] put tx abort:', tx.error);
      resolve();
    };
  });
}

/**
 * Devuelve todos los tracks persistidos. Si IDB no está disponible o
 * está vacío, devuelve `[]` — el caller no necesita distinguir.
 */
export async function idbGetAllTracks(): Promise<IdbTrackRecord[]> {
  const db = await openDb();
  if (!db) return [];
  return new Promise<IdbTrackRecord[]>((resolve) => {
    const tx = db.transaction(STORE_TRACKS, 'readonly');
    const store = tx.objectStore(STORE_TRACKS);
    const req = store.getAll();
    req.onsuccess = (): void => {
      const out = (req.result as IdbTrackRecord[] | undefined) ?? [];
      resolve(out);
    };
    req.onerror = (): void => {
      console.warn('[radio/idb] getAll error:', req.error);
      resolve([]);
    };
  });
}

/** Borra todos los tracks persistidos. */
export async function idbClearTracks(): Promise<void> {
  const db = await openDb();
  if (!db) return;
  await new Promise<void>((resolve) => {
    const tx = db.transaction(STORE_TRACKS, 'readwrite');
    tx.objectStore(STORE_TRACKS).clear();
    tx.oncomplete = (): void => resolve();
    tx.onerror = (): void => {
      console.warn('[radio/idb] clear error:', tx.error);
      resolve();
    };
  });
}
